import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './AdminTagManagement.css';
import SvgIcon from '../../components/SvgIcon';

const AdminTagManagement = ({ onBack, onSwitchView, isHubChild = false }) => {
  // Main Data States
  const [tags, setTags] = useState([]);
  const [activeTagId, setActiveTagId] = useState(null);
  const [allocatedCourses, setAllocatedCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Project Tag Dropdown & Search State
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const tagDropdownRef = useRef(null);
  const tagSearchInputRef = useRef(null);

  // Form State
  const [tagCode, setTagCode] = useState('');
  const [tagName, setTagName] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [maxQuota, setMaxQuota] = useState('');
  const [formError, setFormError] = useState('');
  const [codeAvailability, setCodeAvailability] = useState(null); // 'available' | 'taken' | null
  const [isCreating, setIsCreating] = useState(false);

  // Course Pool Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'alpha'
  const [selectedCourseIds, setSelectedCourseIds] = useState(new Set());
  const [batchAssigning, setBatchAssigning] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  // Drag and Drop States
  const [draggedCourseId, setDraggedCourseId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => {
      setToast(prev => (prev?.msg === msg ? null : prev));
    }, 4000);
  }, []);

  // Fetch Tags List
  const fetchTags = useCallback(async (selectTagId = null) => {
    try {
      const res = await fetch('/api/admin/tags');
      const data = await res.json();
      if (data.status === 'success') {
        setTags(data.data);
        if (selectTagId) {
          setActiveTagId(selectTagId);
        } else if (!activeTagId && data.data.length > 0) {
          setActiveTagId(data.data[0].UserTagID);
        }
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      showToast('Failed to load project tags', 'error');
    }
  }, [activeTagId, showToast]);

  // Fetch Allocated Courses for Active Tag
  const fetchAllocatedCourses = useCallback(async (tagId) => {
    if (!tagId) {
      setAllocatedCourses([]);
      return;
    }
    try {
      const res = await fetch(`/api/admin/tags/${tagId}/courses`);
      const data = await res.json();
      if (data.status === 'success') {
        setAllocatedCourses(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch allocated courses:', err);
    }
  }, []);

  // Fetch Available Courses
  const fetchAvailableCourses = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/courses/available');
      const data = await res.json();
      if (data.status === 'success') {
        setAvailableCourses(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch available courses:', err);
    }
  }, []);

  // Initial Load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchTags(), fetchAvailableCourses()]);
      setLoading(false);
    };
    init();
  }, []);

  // Refresh allocated courses when activeTagId changes
  useEffect(() => {
    if (activeTagId) {
      fetchAllocatedCourses(activeTagId);
    }
  }, [activeTagId, fetchAllocatedCourses]);

  // Check code uniqueness inline
  useEffect(() => {
    const clean = tagCode.trim().toUpperCase();
    if (!clean) {
      setCodeAvailability(null);
      setFormError('');
      return;
    }
    const exists = tags.some(t => t.TagCode.toUpperCase() === clean);
    if (exists) {
      setCodeAvailability('taken');
      setFormError(`Project code "${clean}" is already in use.`);
    } else {
      setCodeAvailability('available');
      setFormError('');
    }
  }, [tagCode, tags]);

  // Active Tag Object
  const activeTag = useMemo(() => {
    return tags.find(t => t.UserTagID === activeTagId) || null;
  }, [tags, activeTagId]);

  // Filtered Tags for Searchable Dropdown
  const filteredTags = useMemo(() => {
    if (!tagSearchQuery.trim()) return tags;
    const q = tagSearchQuery.trim().toLowerCase();
    return tags.filter(t => 
      (t.TagCode || '').toLowerCase().includes(q) ||
      (t.TagName || '').toLowerCase().includes(q) ||
      (t.Description || '').toLowerCase().includes(q)
    );
  }, [tags, tagSearchQuery]);

  // Close tag dropdown on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setIsTagDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsTagDropdownOpen(false);
      }
    };
    if (isTagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTagDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isTagDropdownOpen) {
      setTimeout(() => {
        tagSearchInputRef.current?.focus();
      }, 50);
    }
  }, [isTagDropdownOpen]);

  const handleSelectTag = (tagId) => {
    setActiveTagId(tagId);
    setIsTagDropdownOpen(false);
    setTagSearchQuery('');
  };

  // Copy Project Code Handler
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    setCopiedCode(false);
  }, [activeTagId]);

  const handleCopyProjectCode = useCallback(() => {
    if (!activeTag?.TagCode) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(activeTag.TagCode);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = activeTag.TagCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Failed to copy', err);
      }
      document.body.removeChild(textArea);
    }
    setCopiedCode(true);
    showToast(`Copied "${activeTag.TagCode}" to clipboard!`, 'success');
    setTimeout(() => setCopiedCode(false), 2500);
  }, [activeTag, showToast]);

  // Set of course IDs currently allocated to active tag
  const allocatedIdsSet = useMemo(() => {
    return new Set(allocatedCourses.map(c => c.CourseID));
  }, [allocatedCourses]);

  // Filtered and Sorted Course Pool
  const displayedCourses = useMemo(() => {
    let list = [...availableCourses];

    // Live search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(c => 
        (c.CourseName || '').toLowerCase().includes(q) ||
        (c.CertificationBody || '').toLowerCase().includes(q) ||
        (c.Type || '').toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === 'alpha') {
      list.sort((a, b) => (a.CourseName || '').localeCompare(b.CourseName || ''));
    } else {
      // 'recent' by CourseID descending
      list.sort((a, b) => (b.CourseID || 0) - (a.CourseID || 0));
    }

    return list;
  }, [availableCourses, searchQuery, sortBy]);

  // Create Tag Submit Handler
  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!tagCode.trim()) {
      setFormError('Project code is required.');
      return;
    }
    if (!tagName.trim()) {
      setFormError('Tag name is required.');
      return;
    }
    if (!expiryDate) {
      setFormError('Mandatory expiry date is required.');
      return;
    }
    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).format(new Date());
    if (expiryDate < todayStr) {
      setFormError('Expiry date cannot be in the past.');
      return;
    }
    if (codeAvailability === 'taken') {
      return;
    }

    setIsCreating(true);
    setFormError('');

    try {
      const res = await fetch('/api/admin/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tagCode: tagCode.trim().toUpperCase(),
          tagName: tagName.trim(),
          description: description.trim() || null,
          expiryDate: expiryDate || null,
          maxQuota: maxQuota ? parseInt(maxQuota, 10) : null
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast(`Project tag "${data.tag.TagCode}" created successfully!`);
        // Reset form
        setTagCode('');
        setTagName('');
        setDescription('');
        setExpiryDate('');
        setMaxQuota('');
        setCodeAvailability(null);
        // Refresh tags and select newly created tag
        await fetchTags(data.tag.UserTagID);
      } else {
        setFormError(data.message || 'Failed to create tag.');
      }
    } catch (err) {
      console.error('Create tag error:', err);
      setFormError('Connection error. Could not create tag.');
    } finally {
      setIsCreating(false);
    }
  };

  // Assign Course to Active Tag (Optimistic UI)
  const handleAssignCourses = async (courseIds) => {
    if (!activeTagId) {
      showToast('Please select an active tag first.', 'error');
      return;
    }

    const idsToAssign = (Array.isArray(courseIds) ? courseIds : [courseIds])
      .filter(id => !allocatedIdsSet.has(id));

    if (idsToAssign.length === 0) {
      showToast('Selected course(s) are already allocated to this tag.');
      return;
    }

    // Capture previous state for rollback
    const previousAllocated = [...allocatedCourses];
    const previousAvailable = [...availableCourses];

    // Optimistic Update
    const newlyAllocated = availableCourses
      .filter(c => idsToAssign.includes(c.CourseID))
      .map(c => ({ ...c, CourseTagAccessID: Date.now() }));

    setAllocatedCourses(prev => [...prev, ...newlyAllocated]);
    setAvailableCourses(prev => prev.map(c => {
      if (idsToAssign.includes(c.CourseID)) {
        const assigned = c.assignedTags || [];
        const alreadyHas = assigned.some(t => t.UserTagID === activeTagId);
        return {
          ...c,
          assignedTags: alreadyHas ? assigned : [...assigned, {
            UserTagID: activeTag.UserTagID,
            TagID: activeTag.UserTagID,
            TagCode: activeTag.TagCode,
            TagName: activeTag.TagName
          }]
        };
      }
      return c;
    }));

    try {
      const res = await fetch(`/api/admin/tags/${activeTagId}/courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseIds: idsToAssign })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast(`Allocated ${idsToAssign.length} course(s) to "${activeTag.TagCode}".`);
        fetchTags(activeTagId); // Update tag course counts
      } else {
        throw new Error(data.message || 'Server returned error');
      }
    } catch (err) {
      console.error('Failed to assign courses:', err);
      // Rollback
      setAllocatedCourses(previousAllocated);
      setAvailableCourses(previousAvailable);
      showToast('Failed to assign course. Changes rolled back.', 'error');
    }
  };

  // Remove Course Allocation (Optimistic UI)
  const handleRemoveAllocation = async (courseId) => {
    if (!activeTagId) return;

    // Capture previous state for rollback
    const previousAllocated = [...allocatedCourses];
    const previousAvailable = [...availableCourses];

    // Optimistic Update
    setAllocatedCourses(prev => prev.filter(c => c.CourseID !== courseId));
    setAvailableCourses(prev => prev.map(c => {
      if (c.CourseID === courseId) {
        return {
          ...c,
          assignedTags: (c.assignedTags || []).filter(t => t.UserTagID !== activeTagId)
        };
      }
      return c;
    }));

    try {
      const res = await fetch(`/api/admin/tags/${activeTagId}/courses/${courseId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast('Course allocation removed.');
        fetchTags(activeTagId); // Update tag counts
      } else {
        throw new Error(data.message || 'Server error');
      }
    } catch (err) {
      console.error('Failed to remove course allocation:', err);
      // Rollback
      setAllocatedCourses(previousAllocated);
      setAvailableCourses(previousAvailable);
      showToast('Failed to remove allocation. Changes rolled back.', 'error');
    }
  };

  // Remove All Course Allocations for Active Tag (Optimistic UI)
  const handleRemoveAllAllocations = async () => {
    if (!activeTagId || allocatedCourses.length === 0 || isRemovingAll) return;

    // Capture previous state for rollback
    const previousAllocated = [...allocatedCourses];
    const previousAvailable = [...availableCourses];
    const count = allocatedCourses.length;

    setIsRemovingAll(true);

    // Optimistic Update: clear allocations and strip this active tag from available course assignedTags
    setAllocatedCourses([]);
    setAvailableCourses(prev => prev.map(c => ({
      ...c,
      assignedTags: (c.assignedTags || []).filter(t => t.UserTagID !== activeTagId)
    })));

    try {
      const res = await fetch(`/api/admin/tags/${activeTagId}/courses`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await res.json().catch(() => ({ status: 'success' }));
        showToast(`Removed all ${count} allocated course(s) from "${activeTag?.TagCode || 'tag'}".`);
        fetchTags(activeTagId); // Update tag course counts
      } else if (res.status === 404) {
        // Fallback for earlier server route: delete individually
        await Promise.all(
          previousAllocated.map(c => 
            fetch(`/api/admin/tags/${activeTagId}/courses/${c.CourseID}`, { method: 'DELETE' })
          )
        );
        showToast(`Removed all ${count} allocated course(s) from "${activeTag?.TagCode || 'tag'}".`);
        fetchTags(activeTagId);
      } else {
        const data = await res.json().catch(() => ({ message: 'Server error' }));
        throw new Error(data.message || 'Server error');
      }
    } catch (err) {
      console.error('Failed to remove all course allocations:', err);
      // Rollback
      setAllocatedCourses(previousAllocated);
      setAvailableCourses(previousAvailable);
      showToast('Failed to remove allocations. Changes rolled back.', 'error');
    } finally {
      setIsRemovingAll(false);
    }
  };

  // Delete Active Project Tag
  const handleDeleteActiveTag = async () => {
    if (!activeTag || isDeletingTag) return;

    const confirmMsg = `Are you sure you want to remove project tag "${activeTag.TagCode}" (${activeTag.TagName})? This will unassign all courses and learners associated with this tag.`;
    if (window.confirm && !window.confirm(confirmMsg)) {
      return;
    }

    setIsDeletingTag(true);
    const tagCodeToRemove = activeTag.TagCode;
    const tagIdToRemove = activeTag.UserTagID;

    try {
      const res = await fetch(`/api/admin/tags/${tagIdToRemove}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await res.json().catch(() => ({ status: 'success' }));
        showToast(`Project tag "${tagCodeToRemove}" removed successfully.`);

        // Update tags list
        const remainingTags = tags.filter(t => t.UserTagID !== tagIdToRemove);
        setTags(remainingTags);

        // Select first remaining tag or null
        if (remainingTags.length > 0) {
          setActiveTagId(remainingTags[0].UserTagID);
        } else {
          setActiveTagId(null);
          setAllocatedCourses([]);
        }

        // Refresh available courses pool so tag badges update
        fetchAvailableCourses();
      } else {
        const data = await res.json().catch(() => ({ message: 'Server error' }));
        throw new Error(data.message || 'Failed to remove project tag');
      }
    } catch (err) {
      console.error('Failed to remove tag:', err);
      showToast(err.message || 'Failed to remove project tag', 'error');
    } finally {
      setIsDeletingTag(false);
    }
  };

  // Batch Assign Selected Courses
  const handleBatchAssign = async () => {
    if (selectedCourseIds.size === 0) return;
    setBatchAssigning(true);
    await handleAssignCourses(Array.from(selectedCourseIds));
    setSelectedCourseIds(new Set());
    setBatchAssigning(false);
  };

  // Toggle selection for batch
  const handleToggleSelectCourse = (courseId) => {
    setSelectedCourseIds(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  // Clear course selection when active tag changes
  useEffect(() => {
    setSelectedCourseIds(new Set());
  }, [activeTagId]);

  // Selectable courses in the pool (excluding those already allocated to active tag)
  const selectableCourses = useMemo(() => {
    return displayedCourses.filter(c => !allocatedIdsSet.has(c.CourseID));
  }, [displayedCourses, allocatedIdsSet]);

  // Check if all selectable courses are currently ticked
  const isAllSelected = useMemo(() => {
    if (selectableCourses.length === 0) return false;
    return selectableCourses.every(c => selectedCourseIds.has(c.CourseID));
  }, [selectableCourses, selectedCourseIds]);

  // Toggle select / deselect all courses directly
  const handleToggleSelectAll = () => {
    if (selectableCourses.length === 0) return;

    setSelectedCourseIds(prev => {
      const next = new Set(prev);
      if (isAllSelected) {
        // Deselect all selectable courses currently displayed
        selectableCourses.forEach(c => next.delete(c.CourseID));
      } else {
        // Select all selectable courses currently displayed
        selectableCourses.forEach(c => next.add(c.CourseID));
      }
      return next;
    });
  };

  // HTML5 Drag and Drop Handlers
  const handleDragStart = (e, courseId) => {
    setDraggedCourseId(courseId);
    e.dataTransfer.setData('text/plain', courseId.toString());
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  const handleDragEnd = () => {
    setDraggedCourseId(null);
    setIsDragOver(false);
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDropZoneDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDropZoneDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const data = e.dataTransfer.getData('text/plain');
    const courseId = parseInt(data, 10) || draggedCourseId;
    if (courseId) {
      handleAssignCourses([courseId]);
    }
  };

  // Certification badge styling helper
  const getCertBadgeClass = (body) => {
    const b = (body || '').toLowerCase();
    if (b.includes('comptia')) return 'cert-comptia';
    if (b.includes('isc2')) return 'cert-isc2';
    if (b.includes('blackberry')) return 'cert-blackberry';
    return 'cert-default';
  };

  return (
    <div className="tag-management-container" data-testid="admin-tag-management-page">
      {/* ── Top Bar ── */}
      <header className="tag-top-bar">
        <div className="tag-top-bar-left">
          <div className="tag-breadcrumbs">
            <span 
              className="tag-breadcrumb-link" 
              onClick={() => onSwitchView ? onSwitchView('adminCourses') : onBack && onBack()}
              data-testid="tag-breadcrumb-courses"
            >
              Courses
            </span>
            <span>/</span>
            {isHubChild ? (
              <>
                <span 
                  className="tag-breadcrumb-link" 
                  onClick={onBack}
                  data-testid="tag-breadcrumb-hub"
                >
                  Project Tag Allocation
                </span>
                <span>/</span>
              </>
            ) : null}
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Manage/Create Project Tag</span>
          </div>
          <h1 className="tag-page-title">
            <SvgIcon name="tag" size={24} />
            <span>Project Tag & Special-Project Allocation</span>
          </h1>
          <p className="tag-page-subtitle">
            Create project tags, manage restricted cohorts, and drag-and-drop courses to configure access eligibility.
          </p>
        </div>
        <div className="tag-top-bar-right">
          <button className="btn-secondary" onClick={onBack} data-testid="back-to-courses-btn">
            <SvgIcon name="arrowLeft" size={16} />
            <span>{isHubChild ? 'Back to Tag Hub' : 'Back to Courses'}</span>
          </button>
        </div>
      </header>

      {/* ── Toast Notification ── */}
      {toast && (
        <div className={`tag-toast ${toast.type}`} data-testid="tag-toast-message">
          <SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={18} />
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ── Split-Screen Grid ── */}
      <div className="tag-split-grid">
        {/* ═══════════════════════════════════════════════════════════════════
            LEFT HALF: Tag Creation & Active Tag Inspector
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="tag-col-left">
          
          {/* Card 1: Create Project Tag */}
          <div className="tag-card" data-testid="create-tag-card">
            <div className="tag-card-header">
              <h2 className="tag-card-title">
                <SvgIcon name="tag" size={18} />
                <span>Create New Project Tag</span>
              </h2>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>BCWA-17</span>
            </div>

            <form className="tag-form" onSubmit={handleCreateTag}>
              <div className="tag-form-row two-col">
                <div className="tag-form-group">
                  <label className="tag-label" htmlFor="tagCode">
                    <span>Project Code <span className="required">*</span></span>
                    {codeAvailability === 'available' && <span className="tag-feedback success">✓ Available</span>}
                    {codeAvailability === 'taken' && <span className="tag-feedback error">✕ Taken</span>}
                  </label>
                  <input
                    id="tagCode"
                    type="text"
                    className={`tag-input ${codeAvailability === 'taken' ? 'error' : ''}`}
                    placeholder="E.G. CYBER-GOV-2026"
                    value={tagCode}
                    onChange={(e) => setTagCode(e.target.value.toUpperCase())}
                    data-testid="tag-code-input"
                    required
                  />
                </div>

                <div className="tag-form-group">
                  <label className="tag-label" htmlFor="tagName">
                    <span>Tag Name <span className="required">*</span></span>
                  </label>
                  <input
                    id="tagName"
                    type="text"
                    className="tag-input"
                    placeholder="E.G. Cyber Gov Cohort 2026"
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                    data-testid="tag-name-input"
                    required
                  />
                </div>
              </div>

              <div className="tag-form-group">
                <label className="tag-label" htmlFor="description">Description</label>
                <textarea
                  id="description"
                  className="tag-textarea"
                  rows="2"
                  placeholder="Describe the cohort, department, or special project..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="tag-description-input"
                />
              </div>

              <div className="tag-form-row two-col">
                <div className="tag-form-group">
                  <label className="tag-label" htmlFor="expiryDate">Expiry Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    id="expiryDate"
                    type="date"
                    required
                    min={new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).format(new Date())}
                    className="tag-input"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    data-testid="tag-expiry-input"
                  />
                </div>

                <div className="tag-form-group">
                  <label className="tag-label" htmlFor="maxQuota">Max Quota (Optional)</label>
                  <input
                    id="maxQuota"
                    type="number"
                    min="1"
                    className="tag-input"
                    placeholder="Unlimited if blank"
                    value={maxQuota}
                    onChange={(e) => setMaxQuota(e.target.value)}
                    data-testid="tag-quota-input"
                  />
                </div>
              </div>

              {formError && (
                <div className="tag-feedback error" style={{ fontWeight: 600 }}>
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={isCreating || codeAvailability === 'taken' || !tagCode.trim() || !tagName.trim()}
                data-testid="create-tag-submit-btn"
              >
                <SvgIcon name="check" size={16} />
                <span>{isCreating ? 'Creating Tag...' : 'Create Project Tag'}</span>
              </button>
            </form>
          </div>

          {/* Card 2: Active Tag Selector & Allocated Courses Drop Zone */}
          <div className="tag-card" data-testid="active-tag-card">
            <div className="tag-card-header">
              <h2 className="tag-card-title">
                <SvgIcon name="shield" size={18} />
                <span>Active Tag Allocation Inspector</span>
              </h2>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5' }}>
                {tags.length} Active Tags
              </span>
            </div>

            {/* Searchable Project Tag Dropdown */}
            <div className="tag-dropdown-container" ref={tagDropdownRef} data-testid="tag-selector-dropdown">
              <label className="tag-label" htmlFor="tag-dropdown-button" style={{ marginBottom: '0.4rem' }}>
                <span>Select Project Tag:</span>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                  {filteredTags.length} of {tags.length} tags
                </span>
              </label>

              {/* Dropdown Trigger Button */}
              <button
                id="tag-dropdown-button"
                type="button"
                className={`tag-dropdown-trigger ${isTagDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsTagDropdownOpen(prev => !prev)}
                data-testid="tag-dropdown-trigger"
                aria-haspopup="listbox"
                aria-expanded={isTagDropdownOpen}
              >
                <div className="tag-dropdown-trigger-content">
                  {activeTag ? (
                    <div className="tag-dropdown-selected-tag">
                      <span className="tag-code-badge">{activeTag.TagCode}</span>
                      <span className="tag-dropdown-selected-name">{activeTag.TagName}</span>
                      <span className="tag-dropdown-selected-count">
                        <SvgIcon name="book" size={13} />
                        <span>{activeTag.allocatedCoursesCount || 0} courses</span>
                      </span>
                    </div>
                  ) : (
                    <span className="tag-dropdown-placeholder">Select a project tag...</span>
                  )}
                </div>
                <span className={`tag-dropdown-arrow ${isTagDropdownOpen ? 'rotated' : ''}`}>
                  <SvgIcon name="chevronDown" size={18} />
                </span>
              </button>

              {/* Dropdown Menu */}
              {isTagDropdownOpen && (
                <div className="tag-dropdown-menu" data-testid="tag-dropdown-menu" role="listbox">
                  {/* Search Input Bar */}
                  <div className="tag-dropdown-search-box">
                    <span className="tag-dropdown-search-icon">
                      <SvgIcon name="search" size={15} />
                    </span>
                    <input
                      ref={tagSearchInputRef}
                      type="text"
                      className="tag-dropdown-search-input"
                      placeholder="Search tags by code, name, or description..."
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      data-testid="tag-dropdown-search-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {tagSearchQuery && (
                      <button
                        type="button"
                        className="tag-dropdown-clear-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTagSearchQuery('');
                          tagSearchInputRef.current?.focus();
                        }}
                        title="Clear search"
                        data-testid="tag-dropdown-clear-btn"
                      >
                        <SvgIcon name="x" size={13} />
                      </button>
                    )}
                  </div>

                  {/* Options List */}
                  <div className="tag-dropdown-list" data-testid="tag-dropdown-options-list">
                    {filteredTags.length === 0 ? (
                      <div className="tag-dropdown-empty" data-testid="tag-dropdown-empty">
                        <SvgIcon name="alertCircle" size={18} />
                        <span>No project tags matching "{tagSearchQuery}"</span>
                      </div>
                    ) : (
                      filteredTags.map(t => {
                        const isSelected = t.UserTagID === activeTagId;
                        return (
                          <div
                            key={t.UserTagID}
                            className={`tag-dropdown-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectTag(t.UserTagID)}
                            data-testid={`tag-option-${t.TagCode}`}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="tag-dropdown-item-left">
                              <div className="tag-dropdown-item-main">
                                <span className="tag-code-badge">{t.TagCode}</span>
                                <strong className="tag-dropdown-item-name">{t.TagName}</strong>
                                {t.isExpired && (
                                  <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '1px 5px', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 700 }}>
                                    EXPIRED
                                  </span>
                                )}
                              </div>
                              {t.Description && (
                                <p className="tag-dropdown-item-desc">{t.Description}</p>
                              )}
                            </div>
                            <div className="tag-dropdown-item-right">
                              <span className="tag-dropdown-item-chip" title="Courses allocated">
                                <SvgIcon name="book" size={12} />
                                <span>{t.allocatedCoursesCount || 0}</span>
                              </span>
                              {isSelected && (
                                <span className="tag-dropdown-item-check" title="Currently selected">
                                  <SvgIcon name="check" size={16} />
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Project Code Display Frame with Copy Button */}
            {activeTag && (
              <div className="project-code-copy-frame" data-testid="project-code-copy-frame">
                <div className="project-code-copy-info">
                  <span className="project-code-copy-label">Project Code:</span>
                  <span className="project-code-copy-value" data-testid="active-project-code-value">
                    {activeTag.TagCode}
                  </span>
                </div>
                <button
                  type="button"
                  className={`project-code-copy-btn ${copiedCode ? 'copied' : ''}`}
                  onClick={handleCopyProjectCode}
                  title={`Copy "${activeTag.TagCode}" to clipboard`}
                  data-testid="copy-project-code-btn"
                >
                  <SvgIcon name={copiedCode ? 'check' : 'clipboard'} size={15} />
                  <span>{copiedCode ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            )}

            {/* Active Tag Details Box */}
            {activeTag ? (
              <div className="tag-details-box" data-testid="active-tag-details">
                <div className="tag-details-header">
                  <div className="tag-details-title-wrap">
                    <span className="tag-code-badge">{activeTag.TagCode}</span>
                    <strong className="tag-details-name">{activeTag.TagName}</strong>
                  </div>
                  <div className="tag-details-header-actions">
                    <div className="tag-meta-stats">
                      <div className="tag-meta-stat-item" title="Courses allocated to this tag">
                        <SvgIcon name="book" size={14} />
                        <span>{allocatedCourses.length} Courses</span>
                      </div>
                      <div className="tag-meta-stat-item" title="Learners with this tag">
                        <SvgIcon name="users" size={14} />
                        <span>{activeTag.registeredLearnersCount || 0} Learners</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tag-remove-btn"
                      onClick={handleDeleteActiveTag}
                      disabled={isDeletingTag}
                      title={`Remove project tag "${activeTag.TagCode}"`}
                      data-testid="remove-active-tag-btn"
                    >
                      <SvgIcon name="trash" size={13} />
                      <span>{isDeletingTag ? 'Removing...' : 'Remove Tag'}</span>
                    </button>
                  </div>
                </div>
                {activeTag.Description && (
                  <p className="tag-details-desc">{activeTag.Description}</p>
                )}
                {activeTag.ExpiryDate && (
                  <div style={{ fontSize: '0.75rem', color: activeTag.isExpired ? '#ef4444' : '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>Expires: <strong>{activeTag.ExpiryDate}</strong></span>
                    {activeTag.isExpired && (
                      <span className="badge badge-expired" style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }}>
                        EXPIRED
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>
                No project tag selected. Create or select a tag above.
              </div>
            )}

            {/* Visual Drop Zone for Course Allocation */}
            <div
              className={`drop-zone ${isDragOver ? 'drag-hover' : ''}`}
              onDragOver={handleDropZoneDragOver}
              onDragLeave={handleDropZoneDragLeave}
              onDrop={handleDropZoneDrop}
              data-testid="allocated-courses-drop-zone"
            >
              <div className="drop-zone-header">
                <span className="drop-zone-title">
                  <SvgIcon name="download" size={16} />
                  <span>Allocated Courses Drop Zone</span>
                </span>
                <div className="drop-zone-header-actions">
                  <span className="drop-zone-badge">
                    {allocatedCourses.length} Allocated
                  </span>
                  {allocatedCourses.length > 0 && (
                    <button
                      type="button"
                      className="drop-zone-remove-all-btn"
                      onClick={handleRemoveAllAllocations}
                      disabled={isRemovingAll}
                      title={`Remove all ${allocatedCourses.length} allocated courses from this tag`}
                      data-testid="remove-all-allocated-btn"
                    >
                      <SvgIcon name="trash" size={13} />
                      <span>{isRemovingAll ? 'Removing...' : 'Remove All'}</span>
                    </button>
                  )}
                </div>
              </div>

              {allocatedCourses.length === 0 ? (
                <div className="drop-zone-empty">
                  <div className="drop-zone-empty-icon">
                    <SvgIcon name="download" size={20} />
                  </div>
                  <span className="drop-zone-empty-text">Drag course cards from the right panel here</span>
                  <span className="drop-zone-empty-sub">or click "+ Assign" on any course card to link it to {activeTag?.TagCode || 'this tag'}</span>
                </div>
              ) : (
                <div className="allocated-chips-grid" data-testid="allocated-chips-container">
                  {allocatedCourses.map(course => (
                    <div
                      key={course.CourseID}
                      className="allocated-chip"
                      data-testid={`allocated-chip-${course.CourseID}`}
                    >
                      <span className="chip-cert">{course.CertificationBody}</span>
                      <span>{course.CourseName}</span>
                      <button
                        className="chip-remove-btn"
                        title="Remove allocation"
                        onClick={() => handleRemoveAllocation(course.CourseID)}
                        data-testid={`remove-course-chip-${course.CourseID}`}
                      >
                        <SvgIcon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions directly under the Allocated Courses Drop Zone */}
            {allocatedCourses.length > 0 && (
              <div className="drop-zone-bottom-actions">
                <button
                  type="button"
                  className="drop-zone-remove-all-btn"
                  onClick={handleRemoveAllAllocations}
                  disabled={isRemovingAll}
                  title={`Remove all ${allocatedCourses.length} allocated courses from this tag`}
                  data-testid="remove-all-allocated-bottom-btn"
                >
                  <SvgIcon name="trash" size={13} />
                  <span>{isRemovingAll ? 'Removing...' : 'Remove All'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            RIGHT HALF: Course Pool & Discovery
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="tag-card" data-testid="course-pool-card">
          <div className="tag-card-header">
            <h2 className="tag-card-title">
              <SvgIcon name="book" size={18} />
              <span>Available Course Pool</span>
            </h2>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
              Showing {displayedCourses.length} courses
            </span>
          </div>

          {/* Search, Filter, Sort, and Batch Controls */}
          <div className="course-pool-controls">
            <div className="course-search-wrapper">
              <span className="course-search-icon">
                <SvgIcon name="search" size={16} />
              </span>
              <input
                type="text"
                className="course-search-input"
                placeholder="Search courses by name, body (CompTIA, ISC2...), or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="course-pool-search-input"
              />
            </div>

            <div className="course-filter-row">
              <div className="course-filter-group">
                <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Sort:</label>
                <select
                  className="tag-select"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  data-testid="course-pool-sort-select"
                >
                  <option value="recent">Most Recent (CourseID)</option>
                  <option value="alpha">Alphabetical (A - Z)</option>
                </select>
              </div>

              <div className="course-filter-actions">
                {/* Select All / Deselect All Button */}
                <button
                  type="button"
                  className={`course-select-all-btn ${isAllSelected ? 'all-selected' : ''}`}
                  onClick={handleToggleSelectAll}
                  disabled={selectableCourses.length === 0}
                  title={selectableCourses.length === 0 ? 'No courses available to select' : (isAllSelected ? 'Untick all courses' : 'Tick all courses directly')}
                  aria-label={isAllSelected ? 'Deselect all courses' : 'Tick all courses directly'}
                  data-testid="select-all-courses-btn"
                >
                  <SvgIcon name={isAllSelected ? 'x' : 'checkSquare'} size={14} />
                  <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
                </button>

                {/* Batch Assign Selected Button */}
                <button
                  className="course-batch-btn"
                  onClick={handleBatchAssign}
                  disabled={selectedCourseIds.size === 0 || batchAssigning || !activeTagId}
                  data-testid="batch-assign-btn"
                >
                  <SvgIcon name="checkSquare" size={14} />
                  <span>
                    {batchAssigning
                      ? 'Assigning...'
                      : `Assign Selected (${selectedCourseIds.size}) to ${activeTag ? activeTag.TagCode : 'Tag'}`}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Course Cards List (Draggable Cards) */}
          <div className="course-cards-list" data-testid="course-cards-list">
            {displayedCourses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                No courses match your search criteria.
              </div>
            ) : (
              displayedCourses.map(course => {
                const isAllocatedToActive = allocatedIdsSet.has(course.CourseID);
                const isDraggingThis = draggedCourseId === course.CourseID;
                const isChecked = selectedCourseIds.has(course.CourseID);

                return (
                  <div
                    key={course.CourseID}
                    className={`course-pool-card ${isAllocatedToActive ? 'allocated-to-active' : ''} ${isDraggingThis ? 'dragging' : ''}`}
                    draggable={!isAllocatedToActive}
                    onDragStart={(e) => handleDragStart(e, course.CourseID)}
                    onDragEnd={handleDragEnd}
                    data-testid={`draggable-course-card-${course.CourseID}`}
                  >
                    {/* Checkbox for batch multi-select */}
                    <input
                      type="checkbox"
                      className="course-card-checkbox"
                      checked={isChecked}
                      disabled={isAllocatedToActive}
                      onChange={() => handleToggleSelectCourse(course.CourseID)}
                      data-testid={`course-checkbox-${course.CourseID}`}
                    />

                    {/* Card Content */}
                    <div className="course-card-body">
                      <div className="course-card-top">
                        <span className={`course-cert-badge ${getCertBadgeClass(course.CertificationBody)}`}>
                          {course.CertificationBody}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          ID #{course.CourseID}
                        </span>
                      </div>
                      <div className="course-card-name" title={course.CourseName}>
                        {course.CourseName}
                      </div>

                      {/* Tag Status Badges */}
                      <div className="course-card-tags">
                        {(!course.assignedTags || course.assignedTags.length === 0) ? (
                          <span className="tag-status-pill pill-untagged">Untagged (Public)</span>
                        ) : (
                          course.assignedTags.map((t, idx) => {
                            const isThisActiveTag = t.UserTagID === activeTagId;
                            return (
                              <span
                                key={t.UserTagID || t.TagID || t.TagCode || idx}
                                className={`tag-status-pill ${isThisActiveTag ? 'pill-active-tag' : 'pill-allocated'}`}
                              >
                                {isThisActiveTag ? `✓ ${t.TagCode}` : t.TagCode}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Action: Direct Assign Button or Checkmark */}
                    <div className="course-card-actions">
                      {isAllocatedToActive ? (
                        <div className="course-assigned-label" data-testid={`assigned-label-${course.CourseID}`}>
                          <SvgIcon name="check" size={16} />
                          <span>Allocated</span>
                        </div>
                      ) : (
                        <button
                          className="course-assign-btn"
                          onClick={() => handleAssignCourses([course.CourseID])}
                          title={`Assign to active tag "${activeTag?.TagCode || ''}"`}
                          disabled={!activeTagId}
                          data-testid={`assign-course-btn-${course.CourseID}`}
                        >
                          <span>+ Assign</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminTagManagement;
